import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { withQuery } from 'ufo'
import FluentDocumentProhibited20Regular from '~icons/fluent/document-prohibited-20-regular'
import FluentImageArrowCounterclockwise20Regular from '~icons/fluent/image-arrow-counterclockwise-20-regular'
import FluentMoreVertical20Filled from '~icons/fluent/more-vertical-20-filled'
import Empty from 'src/components/Empty'
import { UIButton } from 'src/components/ui/Button'
import type { UIMenuItemProps } from 'src/components/ui/Menu'
import { UIMenu, UIMenuItem, UIMenuTrigger } from 'src/components/ui/Menu'
import { UIDialog } from 'src/components/ui/Modal'
import Text from 'src/components/ui/Text'
import type {
  GenerationModelType,
  TrainingTaskExtra,
} from 'src/services/apollo/generated/graphql'
import {
  executeArchiveGenerateModelTrainingTask,
  useGetGenerationModel,
} from 'src/services/apollo/generation-model'
import { track } from 'src/utils/tracker'
import { TrainingTaskStatusChip } from '../../GenerationModelTrainingTask/TrainingTaskStatusChip'
import { TrainingTaskStatus } from '../../ModelTypeChip'

export default function ModelTrainingTasks(props: {
  modelId: string
  modelType?: GenerationModelType
  onClose?: () => void
}) {
  const { t } = useTranslation(['market'])
  const { modelId } = props

  const { data, loading, refetch } = useGetGenerationModel(modelId)

  // Filter out completed tasks
  const trainingTasks =
    data?.loraTrainingTasks?.filter(task => task.status !== 'completed') ?? []

  if (trainingTasks.length < 1)
    return (
      <Empty
        description={t(
          'market:edit-model-form.training-tasks.no-incomplete-placeholder',
        )}
      />
    )

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              {t('market:submit.model-version.version-name')}
            </TableCell>
            <TableCell align="right">
              {t('market:submit.model-version.status')}
            </TableCell>
            <TableCell align="right">
              {t('market:edit-model-form.training-tasks.media-count')}
            </TableCell>
            <TableCell align="right">
              {t('market:submit.model-version.update-time')}
            </TableCell>
            <TableCell align="right">
              {t('market:submit.model-version.actions')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {trainingTasks.length < 1 && loading && <TableRowSkeleton />}

          {trainingTasks.map(task => (
            <TableRow
              key={task.id}
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              <TableCell component="th" scope="row">
                <pre>{task.parameters.title ?? 'Unknown'}</pre>
              </TableCell>
              <TableCell align="right">
                <TrainingTaskStatusChip
                  className="px-1.5 py-0.5 text-xs"
                  trainingTask={task}
                  modelType={props.modelType}
                />
              </TableCell>
              <TableCell align="right">
                {Array.isArray(task.parameters.mediaIds)
                  ? task.parameters.mediaIds.length
                  : 'N/A'}
              </TableCell>
              <TableCell align="right">
                {new Date(task.updatedAt).toLocaleString()}
              </TableCell>
              <TableCell align="right">
                <ModelTrainingTaskEditAction
                  status={task.status}
                  taskId={task.id}
                  modelProps={{ id: props.modelId, type: props.modelType }}
                  taskExtra={task.extra}
                  onClose={props.onClose}
                  updateTable={refetch}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function TableRowSkeleton() {
  return (
    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
      <TableCell component="th" scope="row">
        <Skeleton />
      </TableCell>
      <TableCell align="right">
        <Skeleton />
      </TableCell>
      <TableCell align="right">
        <Skeleton />
      </TableCell>
      <TableCell align="right">
        <Skeleton />
      </TableCell>
    </TableRow>
  )
}

function ModelTrainingTaskEditAction(props: {
  status?: string
  taskId: string
  modelProps?: {
    id?: string
    type?: GenerationModelType
  }
  updateTable?: () => any
  taskExtra?: TrainingTaskExtra
  onClose?: () => void
}) {
  const { t } = useTranslation(['market'])
  const navigate = useNavigate()

  // TODO: replace with useAsyncFn
  const onArchive = async () => {
    track('archive_model_version_click')

    try {
      const dialogConfirm = await UIDialog.confirm({
        heading: t('market:submit.archive-training-task.dialog.title'),
        content: t('market:submit.archive-training-task.dialog.description'),
      })

      if (!dialogConfirm) return

      track('archive_training_task_confirm', {
        modelId: props.modelProps?.id,
        trainingTaskId: props.taskId,
      })

      await executeArchiveGenerateModelTrainingTask({
        id: props.taskId,
      })
      props.updateTable?.()
      toast.success(t('market:submit.notice.training-task-archived'))
    } catch (err: any) {
      toast.error(
        t('market:submit.notice.training-task-archive-failed') +
          ': ' +
          err.message,
      )
    }
  }

  const onReuseDataset = () => {
    navigate(
      withQuery('/train-lora', {
        addToModelId: props.modelProps?.id,
        trainingTaskId: props.taskId,
      }),
    )
    props.onClose?.()
  }

  const menuItems = UIMenu.compact<UIMenuItemProps>([
    // Only failed tasks can be archived
    props.status === TrainingTaskStatus.failed && {
      id: 'archive',
      icon: <FluentDocumentProhibited20Regular className="size-5" />,
      children: <Text i18nKey="market:submit.archive-version.label" />,
      color: 'warning',
      onAction: () => {
        onArchive()
      },
    },
    {
      id: 'retrain',
      icon: <FluentImageArrowCounterclockwise20Regular className="size-5" />,
      children: <Text i18nKey="market:submit.reuse-dataset.label" />,
      onAction: () => {
        onReuseDataset()
      },
    },
  ])

  return (
    <div className="flex gap-2 justify-end">
      {props.status === 'archived' ? (
        <UIButton variant="outlined" disabled>
          <span className="italic">
            {t('market:submit.version-status.archived')}
          </span>
        </UIButton>
      ) : (
        <>
          <UIMenuTrigger>
            <UIButton disabled={menuItems.length < 1} variant="outlined">
              <FluentMoreVertical20Filled className="size-5" />
            </UIButton>
            <UIMenu items={menuItems} placement="bottom right">
              {props => <UIMenuItem {...props} />}
            </UIMenu>
          </UIMenuTrigger>
        </>
      )}
    </div>
  )
}
